import { Editor } from '@tldraw/tldraw'
import { TextBoxShapeUtil } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/TextBoxShapeUtil'
import { TextBoxShape } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/Types'
import { assignWith } from 'lodash'
import { FontRole, TextStyle } from 'types'

export type Style = TextStyle['style'] & {
  line_height?: number | string
  letter_spacing?: number
}

/**
 * Gets all text boxes on the current page.
 *
 * @param editor The editor instance.
 * @returns An array of text boxes.
 */
export const getTextBoxes = (editor: Editor) => {
  return editor.getCurrentPageShapes().filter((shape): shape is TextBoxShape => shape.type === 'textBox')
}

/**
 * Gets the text box with the largest font size.
 *
 * @param textBoxes An array of text boxes.
 * @returns The text box with the largest font size.
 */
export const getBiggestTextBox = (textBoxes: TextBoxShape[]) => {
  return textBoxes.reduce((acc, shape) => {
    if (shape.props.fontSize > acc.props.fontSize) {
      return shape
    }
    return acc
  }, textBoxes[0])
}

/**
 * Applies a text style to a text box shape.
 *
 * @param editor The editor instance.
 * @param shape The text box shape.
 * @param textStyle The text style to apply.
 * @returns void
 */
export const applyStyle = (editor: Editor, shape: TextBoxShape, textStyle: Style) => {
  const shapeUtil = editor.getShapeUtil(shape) as TextBoxShapeUtil
  const currentDimensions = shapeUtil.getDimensions(shape.props)

  const props = assignWith(
    { ...shape.props },
    {
      fontFamily: textStyle.font_family,
      italic: textStyle.italic,
      fontWeight: textStyle.font_weight,
      lineHeight: textStyle.line_height,
      letterSpacing: textStyle.letter_spacing,
      uppercase: textStyle.uppercase,
    },
    (shapeValue, kitValue) => {
      if (kitValue === null || kitValue === undefined) {
        return shapeValue
      }
    },
  )

  const newShape = fitTextBox(editor, { ...shape, props }, currentDimensions)

  editor.updateShape(newShape)
}

/**
 * Scales the font size of a text box shape to fit within the given bounds.
 * @param editor The editor instance.
 * @param shape The text box shape.
 * @param bounds The bounds to fit the text box shape within.
 * @returns The scaled font size.
 */
const scaleFontSizeToFit = (editor: Editor, shape: TextBoxShape, bounds: { width: number; height: number }) => {
  const shapeUtil = editor.getShapeUtil('textBox') as TextBoxShapeUtil
  const { props } = shape
  let newProps = { ...props }
  let dimensions = shapeUtil.getDimensions(newProps)

  while (bounds.width < dimensions.width || bounds.height < dimensions.height) {
    if (props.fontSize <= 1) break
    newProps.fontSize -= 1
    dimensions = shapeUtil.getDimensions({ ...newProps })
  }

  return newProps.fontSize
}

/**
 * Fits a text box shape within the given bounds.
 * The text box shape is resized to fit within the given bounds while
 * maintaining its font family and vertical alignment. The autoSize
 * property is set to false to prevent the text box from resizing
 * automatically. The width of the text box is set to the width of the
 * bounds.
 *
 * @param editor The editor instance.
 * @param shape The text box shape.
 * @param bounds The bounds to fit the text box shape within.
 * @returns The resized text box shape.
 */
export const fitTextBox = (
  editor: Editor,
  shape: TextBoxShape,
  bounds: { width: number; height: number },
): TextBoxShape => {
  const shapeUtil = editor.getShapeUtil('textBox') as TextBoxShapeUtil
  const fontSize = scaleFontSizeToFit(editor, shape, bounds)
  const newProps = { ...shape.props, fontSize }
  const dimensions = shapeUtil.getDimensions(newProps)

  let y = shape.y
  if (shape.props.verticalAlign === 'middle') {
    y = shape.y + (bounds.height - dimensions.height) / 2
  }
  if (newProps.verticalAlign === 'bottom') {
    y = shape.y + bounds.height - dimensions.height
  }

  return {
    ...shape,
    y,
    props: {
      ...newProps,
      w: bounds.width,
      autoSize: false,
    },
  }
}

/**
 * Detects the style to apply to a text box shape based on its font size and
 * font family. If the shape's font size or font family matches the title font,
 * the title style is applied. Otherwise, the body style is applied.
 *
 * @param shape The text box shape.
 * @param titleProps The font size and font family of the text box with the largest font size.
 * @param styles The available title and body styles.
 * @returns The style to apply to the text box shape.
 */
export const detectStyleForShape = (
  shape: TextBoxShape,
  titleProps: { fontSize: number },
  styles: { titleStyle?: TextStyle['style']; bodyStyle?: TextStyle['style'] },
) => {
  const { titleStyle, bodyStyle } = styles
  if (titleStyle && shape.props.fontSize === titleProps.fontSize) {
    return titleStyle
  }

  return bodyStyle || titleStyle
}

/**
 * Applies text styles to all text boxes on the current page.
 * The text style to apply is determined based on the font size and font family
 * of each text box. Starting with the text box with the largest font size, the
 * title style is applied. For all other text boxes, depending on their font
 * size and font family, the title or body style is applied.
 *
 * @param editor The editor instance.
 * @param textStyles An array of text styles.
 * @param originalTextBoxes The original text boxes. If provided, the text boxes will not be fetched from the editor.
 * @returns void
 */
export const applyTextStyles = (
  editor: Editor,
  textStyles: Array<{ font_role: FontRole; style: Style }>,
  originalTextBoxes?: TextBoxShape[],
) => {
  if (textStyles.length === 0) return

  const textBoxes = originalTextBoxes || getTextBoxes(editor)
  if (textBoxes.length === 0) return

  const titleStyle = textStyles.find(({ font_role }) => font_role === FontRole.Title)
  const bodyStyle = textStyles.find(({ font_role }) => font_role === FontRole.Body)
  const biggestTextBox = getBiggestTextBox(textBoxes)

  textBoxes.forEach((shape) => {
    const styleToApply = detectStyleForShape(shape, biggestTextBox.props, {
      titleStyle: titleStyle?.style,
      bodyStyle: bodyStyle?.style,
    })
    if (!styleToApply) return

    applyStyle(editor, shape, styleToApply)
  })
}
